import { useNavigation, useSales } from "@utilities/hooks"
import { useEffect, useMemo, useState } from "react";

interface Filters {
    paymentMethod: string;
    dateFrom: string;
    dateTo: string;
    minAmount: string;
    maxAmount: string;
}

export const useControllers = () => {

    const { goBack } = useNavigation();
    const { sales, onGetAllSales } = useSales();

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filters>({
        paymentMethod: '',
        dateFrom: '',
        dateTo: '',
        minAmount: '',
        maxAmount: ''
    });

    const activeFiltersCount = useMemo(() => {
        return Object.values(filters).filter(value => value !== '').length;
    }, [filters]);

    const filteredSales = useMemo(() => {
        if (!sales || !Array.isArray(sales)) return [];

        return sales.filter(sale => {
            // Search term filter
            if (searchTerm.trim() && !sale.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Payment method filter
            if (filters.paymentMethod) {
                // Vérifie le nouveau système (paymentMethods)
                if (sale.paymentMethods && sale.paymentMethods.length > 0) {
                    const hasMethod = sale.paymentMethods.some(pm => pm.methode === filters.paymentMethod);
                    if (!hasMethod) return false;
                }
                // Vérifie l'ancien système (paymentMethode)
                else if (sale.paymentMethode?.methode !== filters.paymentMethod) {
                    return false;
                }
            }

            // Date from filter
            if (filters.dateFrom) {
                const saleDate = new Date(sale.createAt);
                const fromDate = new Date(filters.dateFrom);
                if (saleDate < fromDate) return false;
            }

            // Date to filter
            if (filters.dateTo) {
                const saleDate = new Date(sale.createAt);
                const toDate = new Date(filters.dateTo);
                toDate.setHours(23, 59, 59, 999);
                if (saleDate > toDate) return false;
            }

            // Min amount filter
            if (filters.minAmount && sale.totalPay < parseFloat(filters.minAmount)) {
                return false;
            }

            // Max amount filter
            if (filters.maxAmount && sale.totalPay > parseFloat(filters.maxAmount)) {
                return false;
            }

            return true;
        });
    }, [sales, searchTerm, filters]);

    const salesTotals = useMemo(() => {
        if (!filteredSales || filteredSales.length === 0) {
            return {
                count: 0,
                totalToPay: 0,
                totalPaid: 0,
                totalUnpaid: 0
            };
        }

        return filteredSales.reduce((acc, sale) => ({
            count: acc.count + 1,
            totalToPay: acc.totalToPay + (sale.totalToPay || 0),
            totalPaid: acc.totalPaid + (sale.totalPay || 0),
            totalUnpaid: acc.totalUnpaid + ((sale.totalToPay || 0) - (sale.totalPay || 0))
        }), {
            count: 0,
            totalToPay: 0,
            totalPaid: 0,
            totalUnpaid: 0
        });
    }, [filteredSales]);

    useEffect(() => {
        setTotalPages(Math.ceil(filteredSales.length / itemsPerPage));
        setCurrentPage(1);
    }, [filteredSales.length, itemsPerPage]);

    useEffect(() => {
        onGetAllSales()
    }, []);

    const paginatedSales = useMemo(() => {
        if (!filteredSales || filteredSales.length === 0) return [];
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredSales.slice(startIndex, endIndex);
    }, [filteredSales, currentPage, itemsPerPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (key: keyof Filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            paymentMethod: '',
            dateFrom: '',
            dateTo: '',
            minAmount: '',
            maxAmount: ''
        });
    };

    return {
        paginatedSales, goBack,
        currentPage,
        totalPages,
        itemsPerPage,
        salesTotals,
        searchTerm,
        showFilters,
        filters,
        activeFiltersCount,
        setShowFilters,
        handleFilterChange,
        clearFilters,
        handleSearchChange,
        handlePageChange,
        handleItemsPerPageChange
    }
}
